import React, { useEffect, useState } from 'react';
import { agentService, AgentConfig, AgentConfigUpdate, ChannelPreference } from '../services/agentService';

interface AgentConfigPanelProps {
  userId: number;
  onConfigChange?: (config: AgentConfig) => void;
}

const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  boxSizing: 'border-box',
};
const rowStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 };
const cardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: 16,
  background: '#fff',
  marginBottom: 16,
};
const sectionTitleStyle: React.CSSProperties = { fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 4 };
const sectionHintStyle: React.CSSProperties = { fontSize: 12, color: '#6b7280', marginBottom: 14 };

type ProactivePreset = {
  key: string;
  label: string;
  description: string;
  values: {
    proactive_enabled: boolean;
    daily_proactive_limit: number | null;
    weekly_proactive_limit: number | null;
    min_minutes_between_interactions: number | null;
    quiet_hours_start: string | null;
    quiet_hours_end: string | null;
  };
};

const PROACTIVE_PRESETS: ProactivePreset[] = [
  {
    key: 'off',
    label: 'Apagado',
    description: 'No se envían mensajes proactivos.',
    values: {
      proactive_enabled: false,
      daily_proactive_limit: 0,
      weekly_proactive_limit: 0,
      min_minutes_between_interactions: null,
      quiet_hours_start: null,
      quiet_hours_end: null,
    },
  },
  {
    key: 'light',
    label: '1 al día · diurno',
    description: '1/día · 7/semana · 9:00–21:00.',
    values: {
      proactive_enabled: true,
      daily_proactive_limit: 1,
      weekly_proactive_limit: 7,
      min_minutes_between_interactions: 480,
      quiet_hours_start: '21:00:00',
      quiet_hours_end: '09:00:00',
    },
  },
  {
    key: 'medium',
    label: '2 al día · diurno',
    description: '2/día · 14/semana · 8:00–22:00.',
    values: {
      proactive_enabled: true,
      daily_proactive_limit: 2,
      weekly_proactive_limit: 14,
      min_minutes_between_interactions: 360,
      quiet_hours_start: '22:00:00',
      quiet_hours_end: '08:00:00',
    },
  },
  {
    key: 'intense',
    label: '4 al día · diurno',
    description: '4/día · 28/semana · 8:00–22:00.',
    values: {
      proactive_enabled: true,
      daily_proactive_limit: 4,
      weekly_proactive_limit: 28,
      min_minutes_between_interactions: 180,
      quiet_hours_start: '22:00:00',
      quiet_hours_end: '08:00:00',
    },
  },
  {
    key: 'weekdays',
    label: 'Sólo laborables (≈5/sem)',
    description: '1/día · 5/semana · 9:00–19:00. Aproximado: el sistema no excluye fines de semana, sólo limita por semana.',
    values: {
      proactive_enabled: true,
      daily_proactive_limit: 1,
      weekly_proactive_limit: 5,
      min_minutes_between_interactions: 480,
      quiet_hours_start: '19:00:00',
      quiet_hours_end: '09:00:00',
    },
  },
];

const presetMatches = (preset: ProactivePreset, config: AgentConfig): boolean => {
  const v = preset.values;
  return (
    v.proactive_enabled === config.proactive_enabled &&
    v.daily_proactive_limit === config.daily_proactive_limit &&
    v.weekly_proactive_limit === config.weekly_proactive_limit &&
    v.min_minutes_between_interactions === config.min_minutes_between_interactions &&
    v.quiet_hours_start === config.quiet_hours_start &&
    v.quiet_hours_end === config.quiet_hours_end
  );
};

const AgentConfigPanel: React.FC<AgentConfigPanelProps> = ({ userId, onConfigChange }) => {
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await agentService.getConfig(userId);
        if (!cancelled) setConfig(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Error al cargar configuración del agente');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const update = <K extends keyof AgentConfig>(key: K, value: AgentConfig[K]) => {
    setConfig(prev => (prev ? { ...prev, [key]: value } : prev));
  };

  const applyPreset = (preset: ProactivePreset) => {
    setConfig(prev => (prev ? { ...prev, ...preset.values } : prev));
  };

  const handleSave = async () => {
    if (!config) return;
    try {
      setSaving(true);
      setError(null);
      const payload: AgentConfigUpdate = {
        enabled: config.enabled,
        proactive_enabled: config.proactive_enabled,
        channel_preference: config.channel_preference,
        daily_proactive_limit: config.daily_proactive_limit,
        weekly_proactive_limit: config.weekly_proactive_limit,
        min_minutes_between_interactions: config.min_minutes_between_interactions,
        quiet_hours_start: config.quiet_hours_start,
        quiet_hours_end: config.quiet_hours_end,
      };
      const updated = await agentService.updateConfig(userId, payload);
      setConfig(updated);
      setSavedAt(Date.now());
      onConfigChange?.(updated);
    } catch (e: any) {
      setError(e?.message || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Cargando configuración del agente...</div>;
  }

  if (error && !config) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#e53e3e' }}>{error}</div>;
  }

  if (!config) return null;

  const numberOrNull = (v: string) => (v === '' ? null : Number(v));
  const timeOrNull = (v: string) => (v === '' ? null : (v.length === 5 ? `${v}:00` : v));
  const timeForInput = (v: string | null) => (v ? v.slice(0, 5) : '');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#222' }}>Configuración del Agente</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Profile #{config.profile_id} · actualizado {new Date(config.updated_at).toLocaleString('es-ES')}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={sectionTitleStyle}>Agente</div>
        <div style={sectionHintStyle}>Control general del agente conversacional.</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fafafa' }}>
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={e => update('enabled', e.target.checked)}
          />
          <span>
            <div style={{ fontWeight: 600, color: '#1f2937' }}>Agente habilitado</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Permite al chat ejecutar acciones y tareas de forma autónoma.</div>
          </span>
        </label>
      </div>

      <div style={cardStyle}>
        <div style={sectionTitleStyle}>Mensajes proactivos</div>
        <div style={sectionHintStyle}>Cuándo y cuánto puede iniciar el agente la conversación con el paciente.</div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fafafa', marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={config.proactive_enabled}
            onChange={e => update('proactive_enabled', e.target.checked)}
          />
          <span>
            <div style={{ fontWeight: 600, color: '#1f2937' }}>Proactivo habilitado</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Permite al sistema enviar mensajes al paciente sin que él lo inicie.</div>
          </span>
        </label>

        <div style={{ marginBottom: 16, maxWidth: 320 }}>
          <label style={labelStyle}>Canal preferido</label>
          <select
            style={inputStyle}
            value={config.channel_preference ?? ''}
            onChange={e => update('channel_preference', (e.target.value || null) as ChannelPreference)}
          >
            <option value="">— Sin preferencia —</option>
            <option value="chat">Chat</option>
            <option value="email">Email</option>
            <option value="both">Ambos</option>
          </select>
        </div>

        <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#374151' }}>Configuraciones rápidas</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {PROACTIVE_PRESETS.map(preset => {
            const active = presetMatches(preset, config);
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => applyPreset(preset)}
                title={preset.description}
                style={{
                  background: active ? '#38b2ac' : '#fff',
                  color: active ? '#fff' : '#1f2937',
                  border: `1px solid ${active ? '#38b2ac' : '#d1d5db'}`,
                  borderRadius: 999,
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {preset.label}
              </button>
            );
          })}
        </div>

        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Ajuste fino</div>
        <div style={rowStyle}>
          <div>
            <label style={labelStyle}>Límite diario</label>
            <input
              type="number"
              min={0}
              style={inputStyle}
              value={config.daily_proactive_limit ?? ''}
              onChange={e => update('daily_proactive_limit', numberOrNull(e.target.value))}
              placeholder="(por defecto)"
            />
          </div>
          <div>
            <label style={labelStyle}>Límite semanal</label>
            <input
              type="number"
              min={0}
              style={inputStyle}
              value={config.weekly_proactive_limit ?? ''}
              onChange={e => update('weekly_proactive_limit', numberOrNull(e.target.value))}
              placeholder="(por defecto)"
            />
          </div>
          <div>
            <label style={labelStyle}>Min. minutos entre interacciones</label>
            <input
              type="number"
              min={0}
              style={inputStyle}
              value={config.min_minutes_between_interactions ?? ''}
              onChange={e => update('min_minutes_between_interactions', numberOrNull(e.target.value))}
              placeholder="(por defecto)"
            />
          </div>
        </div>

        <div style={rowStyle}>
          <div>
            <label style={labelStyle}>Inicio horas silencio</label>
            <input
              type="time"
              style={inputStyle}
              value={timeForInput(config.quiet_hours_start)}
              onChange={e => update('quiet_hours_start', timeOrNull(e.target.value))}
            />
          </div>
          <div>
            <label style={labelStyle}>Fin horas silencio</label>
            <input
              type="time"
              style={inputStyle}
              value={timeForInput(config.quiet_hours_end)}
              onChange={e => update('quiet_hours_end', timeOrNull(e.target.value))}
            />
          </div>
        </div>
      </div>

      {error && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: saving ? '#9ca3af' : '#38b2ac',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            fontWeight: 600,
            fontSize: 14,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
        {savedAt && !saving && (
          <span style={{ fontSize: 13, color: '#10b981' }}>Guardado ✓</span>
        )}
      </div>
    </div>
  );
};

export default AgentConfigPanel;
